import { type Dispatch, type SetStateAction, useCallback, useEffect, useMemo, useState } from "react";

type UseExpandedGroupsParams = {
	/**
	 * Groups that must be open right now regardless of what the user opened by hand — e.g. every
	 * group holding a search match. Expansion is DERIVED from this set rather than snapshotted into
	 * state, which is what makes "clearing the search restores the prior expansion" free: the moment
	 * the set empties, the effective expansion falls back to exactly the user's own choices, with no
	 * restore logic and no risk of clobbering a manual collapse.
	 *
	 * DEFAULT: omitting it means "nothing is auto-expanded", i.e. the hook's original behaviour. This
	 * is not a value being invented for missing data — the absence of an auto set IS the state, and
	 * every pre-existing caller relies on it.
	 */
	autoExpandedGroups?: ReadonlySet<string>;
};

export function useExpandedGroups(params?: UseExpandedGroupsParams): {
	expandedGroups: Set<string>;
	setExpandedGroups: Dispatch<SetStateAction<Set<string>>>;
	toggleGroupExpand: (group: string) => void;
} {
	const autoExpandedGroups = params?.autoExpandedGroups;
	const [userExpandedGroups, setUserExpandedGroups] = useState<Set<string>>(new Set());
	// Auto-expanded groups the user has explicitly collapsed again, so the chevron keeps working
	// mid-search instead of being overruled on the next render.
	const [suppressedAutoGroups, setSuppressedAutoGroups] = useState<Set<string>>(new Set());

	const autoExpandedCount = autoExpandedGroups?.size ?? 0;
	useEffect(() => {
		// Nothing is auto-expanded any more (the search was cleared) — forget the suppressions so the
		// next search starts from a clean slate rather than silently keeping groups shut.
		if (autoExpandedCount === 0) {
			setSuppressedAutoGroups((prev) => (prev.size === 0 ? prev : new Set()));
		}
	}, [autoExpandedCount]);

	const expandedGroups = useMemo<Set<string>>(() => {
		if (!autoExpandedGroups || autoExpandedGroups.size === 0) {
			return userExpandedGroups;
		}
		const effective = new Set(userExpandedGroups);
		for (const groupKey of autoExpandedGroups) {
			if (!suppressedAutoGroups.has(groupKey)) {
				effective.add(groupKey);
			}
		}
		return effective;
	}, [userExpandedGroups, autoExpandedGroups, suppressedAutoGroups]);

	const toggleGroupExpand = useCallback(
		(groupKey: string): void => {
			// Toggle against the EFFECTIVE state, not just the user's own set: an auto-expanded group
			// reads as open on screen, so a click on it must close it.
			const isAutoExpanded = !!autoExpandedGroups?.has(groupKey) && !suppressedAutoGroups.has(groupKey);
			const isExpanded = userExpandedGroups.has(groupKey) || isAutoExpanded;

			if (isExpanded) {
				setUserExpandedGroups((prev) => {
					if (!prev.has(groupKey)) return prev;
					const next = new Set(prev);
					next.delete(groupKey);
					return next;
				});
				if (isAutoExpanded) {
					setSuppressedAutoGroups((prev) => {
						const next = new Set(prev);
						next.add(groupKey);
						return next;
					});
				}
				return;
			}

			setUserExpandedGroups((prev) => {
				const next = new Set(prev);
				next.add(groupKey);
				return next;
			});
			setSuppressedAutoGroups((prev) => {
				if (!prev.has(groupKey)) return prev;
				const next = new Set(prev);
				next.delete(groupKey);
				return next;
			});
		},
		[autoExpandedGroups, suppressedAutoGroups, userExpandedGroups],
	);

	return { expandedGroups, setExpandedGroups: setUserExpandedGroups, toggleGroupExpand };
}
